import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { BNCarSearchApiViewModelCarShortViewModel as CarViewModel } from '../../../../../data/src/lib/generated/api-gateway';
import { roundToMultiple } from '../../core/helpers';
import { Environment, ENVIRONMENT } from '../../environment/environment';
import { FilterService } from '../../filter-service/filter.service';
import {
    LeasingAgreementType,
    LeasingPurpose,
} from '../../leasing/leasing.service';

type PriceMode = 'call for price' | 'cash price' | 'leasing terms';

const fallbackValue = 0;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-product-item-price',
    templateUrl: './product-item-price.component.html',
})
export class ProductItemPriceComponent implements OnChanges, OnDestroy, OnInit {
    private exchangeCarValue = fallbackValue;
    private leasingPrice = fallbackValue;
    private unsubscribe = new Subject<void>();

    @Input() agreement: LeasingAgreementType = 'O';
    @Input() car: CarViewModel;
    @Input() purpose: LeasingPurpose = 'brugtbilprivat';
    @Input() showLeasingPrice = false;

    cashPrice = fallbackValue;
    cashPriceExchanged = fallbackValue;
    downPaymentPrice = fallbackValue;
    isMotorpoint = this.environment.site === 'Motorpoint';
    leasingDuration = 0;
    leasingPriceFormatted = '';
    loanPrice = fallbackValue;
    priceMode: PriceMode | undefined;
    showExchangePrice = false;
    showLoanPrice = false;
    showVatNotice = false;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private filterService: FilterService,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {}

    ngOnChanges() {
        if (this.showLeasingPrice) {
            this.updateLeasingPrice();
            this.priceMode =
                this.isMotorpoint && this.leasingPrice < 1000
                    ? 'call for price'
                    : 'leasing terms';
        } else {
            this.updateCashPrice();
            this.priceMode = this.cashPrice ? 'cash price' : undefined;
        }

        this.showExchangePrice =
            this.priceMode === 'cash price' && !!this.exchangeCarValue;

        this.showLoanPrice =
            !this.isMotorpoint &&
            this.priceMode === 'cash price' &&
            !!this.loanPrice;

        this.showVatNotice =
            this.isMotorpoint && this.purpose === 'brugtbilerhverv';

        this.changeDetectorRef.markForCheck();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    ngOnInit() {
        this.filterService.exchangeCarValue
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((v) => {
                this.exchangeCarValue = v || fallbackValue;
                this.ngOnChanges();
            });
    }

    private updateCashPrice() {
        this.cashPrice = this.car.cashPrice?.data ?? fallbackValue;

        this.cashPriceExchanged = Math.max(
            this.cashPrice - this.exchangeCarValue,
            0
        );

        this.loanPrice =
            this.car.santanderPaymentPerMonth?.data ?? fallbackValue;
    }

    private updateLeasingPrice() {
        this.leasingDuration =
            this.car.leasingDefaultDurationInMonths?.data ?? fallbackValue;

        if (this.isMotorpoint) {
            this.updateMotorpointLeasingPrice();
        } else {
            const cashPrice = this.car.cashPrice?.data ?? fallbackValue;

            const downPaymentPercent =
                this.car.leasingDefaultDownPayment?.data ?? fallbackValue;

            const multiplier = downPaymentPercent / 100;

            this.downPaymentPrice = roundToMultiple(
                cashPrice * multiplier,
                1000
            );

            this.leasingPrice =
                this.car.leasingPricePrivateOperational?.data ?? fallbackValue;

            this.leasingPriceFormatted =
                this.car.leasingPricePrivateOperational?.value ?? '';
        }
    }

    private updateMotorpointLeasingPrice() {
        // Operational / Business
        if (this.agreement === 'O' && this.purpose === 'brugtbilerhverv') {
            this.downPaymentPrice =
                this.car.leasingDownpaymentBusinessOperational?.data ??
                fallbackValue;

            this.leasingPrice =
                this.car.leasingPriceBusinessOperational?.data ?? fallbackValue;

            this.leasingPriceFormatted =
                this.car.leasingPriceBusinessOperational?.value ?? '';
        }
        // Operational / Private
        else if (this.agreement === 'O' && this.purpose === 'brugtbilprivat') {
            this.downPaymentPrice =
                this.car.leasingDownpaymentPrivateOperational?.data ??
                fallbackValue;

            this.leasingPrice =
                this.car.leasingPricePrivateOperational?.data ?? fallbackValue;

            this.leasingPriceFormatted =
                this.car.leasingPricePrivateOperational?.value ?? '';
        }
        // Financial / Business
        else if (this.agreement === 'F' && this.purpose === 'brugtbilerhverv') {
            this.downPaymentPrice =
                this.car.leasingDownpaymentBusinessFinancial?.data ??
                fallbackValue;

            this.leasingPrice =
                this.car.leasingPriceBusinessFinancial?.data ?? fallbackValue;

            this.leasingPriceFormatted =
                this.car.leasingPriceBusinessFinancial?.value ?? '';
        }
        // Financial / Private
        else if (this.agreement === 'F' && this.purpose === 'brugtbilprivat') {
            this.downPaymentPrice =
                this.car.leasingDownpaymentPrivateFinancial?.data ??
                fallbackValue;

            this.leasingPrice =
                this.car.leasingPricePrivateFinancial?.data ?? fallbackValue;

            this.leasingPriceFormatted =
                this.car.leasingPricePrivateFinancial?.value ?? '';
        }
    }
}
